import {
  Body1,
  Button,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add16Filled,
  Add16Regular,
  bundleIcon,
  NotepadEdit20Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useState } from 'react';

import { DATASET_ROLE_TOOLTIP_ANNOTATOR } from '../../../../constants/index.js';
import type { AnnotationDataEntry } from '../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../services/dataHubService.js';
import { withCorrelationId } from '../../../../services/usageContext.js';
import { useScenarioFactory } from '../../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../../telemetry/scenarios.js';
import { useStyles } from '../DatasetAnnotationsTab.style.js';

import { AddAnnotationDataDialog } from './AddAnnotationDataDialog.js';
import { AnnotationDataRow } from './AnnotationDataRow.js';

const AddIcon = bundleIcon(Add16Filled, Add16Regular) as React.FC;

const MAX_DATA_ENTRIES_PAGE_SIZE = 100;

export interface AnnotationDataTableProps {
  dataSetId: string;
  dataSetVersion: number;
  annotationSetId: string;
  schemaName: string;
  isDraft: boolean;
  canEdit: boolean;
  service: DataHubService;
}

export function AnnotationDataTable({
  dataSetId,
  dataSetVersion,
  annotationSetId,
  schemaName,
  isDraft,
  canEdit,
  service,
}: AnnotationDataTableProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();
  const [addOpen, setAddOpen] = useState(false);
  const [entries, setEntries] = useState<AnnotationDataEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    service
      .listAnnotationData({
        dataSetId,
        annotationSetId,
        pageSize: MAX_DATA_ENTRIES_PAGE_SIZE,
        includeCount: false,
      })
      .then((result) => {
        if (!cancelled) {
          setEntries(result.items);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load annotation data',
          );
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [service, dataSetId, annotationSetId, refreshKey]);

  const handleDeleteEntry = useCallback(
    async (dataId: string) => {
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDeleteAnnotationData,
      );
      try {
        await service.deleteAnnotationData(dataSetId, annotationSetId, dataId, {
          extras: withCorrelationId(scenario.queryId),
        });
        scenario.complete('success');
        setEntries((prev) => prev.filter((e) => e.dataId !== dataId));
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setError(err instanceof Error ? err.message : 'Failed to delete entry');
      }
    },
    [service, dataSetId, annotationSetId, scenarioFactory],
  );

  const handleUpdateEntry = useCallback((updated: AnnotationDataEntry) => {
    setEntries((prev) =>
      prev.map((e) => (e.dataId === updated.dataId ? updated : e)),
    );
  }, []);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner size="small" label="Loading annotation data…" />
      </div>
    );
  }

  if (error !== null) {
    return <Body1 className={styles.errorState}>{error}</Body1>;
  }

  return (
    <div>
      <div className={styles.dataTabToolbar}>
        {canEdit && isDraft ? (
          <Button
            appearance="outline"
            size="small"
            icon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add Annotation Data
          </Button>
        ) : !canEdit ? (
          <Tooltip
            content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
            relationship="description"
          >
            <Button
              appearance="outline"
              size="small"
              icon={<AddIcon />}
              disabledFocusable
            >
              Add Annotation Data
            </Button>
          </Tooltip>
        ) : null}
      </div>

      {entries.length === 0 ? (
        <div className={styles.emptyStateContainer}>
          <NotepadEdit20Regular className={styles.emptyStateIcon} />
          <Text className={styles.emptyStateTitle}>No annotation data yet</Text>
          <Text className={styles.emptyStateDescription}>
            {canEdit && isDraft
              ? 'Add an annotation data entry to associate it with a raw item from this dataset.'
              : 'Annotation data is added to draft versions. Create a draft to start adding entries.'}
          </Text>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeader}>
              <th
                className={mergeClasses(
                  styles.tableHeaderCell,
                  styles.chevronColumnCell,
                )}
              />
              <th className={styles.tableHeaderCell}>Name</th>
              <th className={styles.tableHeaderCell}>Category</th>
              <th className={styles.tableHeaderCell}>Raw Data ID</th>
              <th className={styles.tableHeaderCell}>Created</th>
              <th
                className={mergeClasses(
                  styles.tableHeaderCell,
                  styles.actionsColumnCell,
                )}
              />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <AnnotationDataRow
                key={entry.dataId}
                entry={entry}
                dataSetId={dataSetId}
                annotationSetId={annotationSetId}
                service={service}
                isDraft={isDraft}
                canEdit={canEdit}
                onDelete={(dataId) => {
                  void handleDeleteEntry(dataId);
                }}
                onUpdated={handleUpdateEntry}
              />
            ))}
          </tbody>
        </table>
      )}

      <AddAnnotationDataDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        dataSetId={dataSetId}
        dataSetVersion={dataSetVersion}
        annotationSetId={annotationSetId}
        schemaName={schemaName}
        service={service}
        onCreated={refresh}
      />
    </div>
  );
}
